import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useOrganizationContext } from '@/contexts/OrganizationContext';
import type { Organization } from '@/hooks/useOrganizations';

export type UseOrganizationLogoOptions = {
  /** Se informado, upload/remove usam esta org (ex.: tela de detalhe). Senão, usa a org ativa no contexto. */
  organization?: Organization | null;
};

export const useOrganizationLogo = (options?: UseOrganizationLogoOptions) => {
  const [isUploading, setIsUploading] = useState(false);
  const { toast } = useToast();
  const { currentOrg, refreshOrganizations } = useOrganizationContext();
  const org = options?.organization ?? currentOrg;

  const uploadLogo = async (file: File) => {
    if (!org) {
      toast({
        title: "Erro",
        description: "Nenhuma organização selecionada",
        variant: "destructive",
      });
      return null;
    }

    try {
      setIsUploading(true);

      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast({
          title: "Erro",
          description: "Por favor, selecione um arquivo de imagem",
          variant: "destructive",
        });
        return null;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: "Erro",
          description: "O arquivo deve ter no máximo 5MB",
          variant: "destructive",
        });
        return null;
      }

      // Create unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `${org.id}/logo.${fileExt}`;

      // Remove existing logo if any
      if (org.logo_url) {
        const oldFileName = org.logo_url.split('/').pop();
        if (oldFileName) {
          await supabase.storage
            .from('org_logos')
            .remove([`${org.id}/${oldFileName}`]);
        }
      }

      // Upload new logo
      const { error: uploadError } = await supabase.storage
        .from('org_logos')
        .upload(fileName, file, {
          upsert: true,
          cacheControl: '3600',
        });

      if (uploadError) {
        throw uploadError;
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('org_logos')
        .getPublicUrl(fileName);

      // Update organization with new logo URL
      const { error: updateError } = await supabase
        .from('organizations')
        .update({ logo_url: publicUrl })
        .eq('id', org.id);

      if (updateError) {
        throw updateError;
      }

      // Update local organization data
      await refreshOrganizations();

      toast({
        title: "Sucesso",
        description: "Logo da organização atualizada com sucesso",
      });

      return publicUrl;
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast({
        title: "Erro",
        description: "Erro ao fazer upload da logo",
        variant: "destructive",
      });
      return null;
    } finally {
      setIsUploading(false);
    }
  };

  const removeLogo = async () => {
    if (!org?.logo_url) {
      return;
    }

    try {
      setIsUploading(true);

      // Remove from storage
      const fileName = org.logo_url.split('/').pop();
      if (fileName) {
        await supabase.storage
          .from('org_logos')
          .remove([`${org.id}/${fileName}`]);
      }

      // Update organization
      const { error } = await supabase
        .from('organizations')
        .update({ logo_url: null })
        .eq('id', org.id);

      if (error) {
        throw error;
      }

      // Update local organization data
      await refreshOrganizations();

      toast({
        title: "Sucesso",
        description: "Logo removida com sucesso",
      });
    } catch (error) {
      console.error('Error removing logo:', error);
      toast({
        title: "Erro",
        description: "Erro ao remover a logo",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const getOrganizationInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .slice(0, 2)
      .join('')
      .toUpperCase();
  };

  return {
    uploadLogo,
    removeLogo,
    isUploading,
    logoUrl: org?.logo_url,
    organizationName: org?.name || '',
    getOrganizationInitials,
  };
};
